import WebsafeError from 'services/websafe-error';
import { validateKey } from 'utilities/fieldKeyUtils.js';
import { optionsListIsEmpty } from '../SingleEntryOptionsList/logic';
import { GECKO_CONSTS } from 'ui';
import { errorStrings } from './textConsts.js';

const validateOption = (options, value) => {
  return options && options.some(({ id }) => id === value);
};

// http://emailregex.com/
const validateEmailValue = (value) =>
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    value
  );

const isEmpty = (value) => value === '' || value === null;
const isString = (value) => typeof value === 'string' || isEmpty(value);

const validValueForField = {
  [GECKO_CONSTS.FIELD_TYPE_EMPTY]: isEmpty,
  [GECKO_CONSTS.FIELD_TYPE_TEXT]: isString,
  [GECKO_CONSTS.FIELD_TYPE_TEXTAREA]: isString,
  [GECKO_CONSTS.FIELD_TYPE_DISPLAY]: (value) =>
    value === true || value === false,
  [GECKO_CONSTS.FIELD_TYPE_EMAIL]: (value) =>
    isEmpty(value) || validateEmailValue(value),
  [GECKO_CONSTS.FIELD_TYPE_DATE]: (value) => /^[0-9]*$/.test(value),
  [GECKO_CONSTS.FIELD_TYPE_TELEPHONE]: isString,
  [GECKO_CONSTS.FIELD_TYPE_NUMBER]: (value) => !isNaN(Number(value)),
  blurb: isString,
  [GECKO_CONSTS.FIELD_TYPE_SELECT]: (value, field) =>
    validateOption(field.field_options, value),
  [GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI]: (value, field) =>
    Array.isArray(value)
      ? value.every(validateOption.bind(null, field.field_options))
      : false,
};

const hasDuplicateField = (field, fields) => {
  if (fields.length === 0) {
    return null;
  }

  const getDuplicateFieldError = ([propField, ...restOfFieldsToCheck]) => {
    const isDifferentField = propField.id !== field.id;
    const hasSameLabel = propField.label === field.label;
    const hasSameKey = propField.key === field.key;

    if (isDifferentField && hasSameLabel) {
      return new WebsafeError(errorStrings.duplicateLabel(field.label));
    }
    if (isDifferentField && hasSameKey) {
      return new WebsafeError(
        errorStrings.duplicateKey(propField.label, field.key)
      );
    }

    if (restOfFieldsToCheck.length !== 0) {
      return getDuplicateFieldError(restOfFieldsToCheck);
    }

    return null;
  };

  return getDuplicateFieldError(fields);
};

const validateDefaultValue = (type, defaultValue, field) => {
  const validator = validValueForField[type];
  if (defaultValue === undefined || defaultValue === null) return;
  if (!(validator && validator(defaultValue, field))) {
    return new WebsafeError(errorStrings.INVALID_DEFAULT);
  }
};

export const getValidationErrors = (field, fields) => {
  const { label, key, type, field_options, default_value } = field;
  if (!label) {
    return new WebsafeError(errorStrings.MISSING_LABEL);
  }
  if (!key) {
    return new WebsafeError(errorStrings.MISSING_KEY);
  }
  if (!type) {
    return new WebsafeError(errorStrings.MISSING_TYPE);
  }

  const checkOptionsForErrors = (
    emptyErrorMessage,
    invalidOptionErrorMessage
  ) => {
    if (optionsListIsEmpty(field_options)) {
      return new WebsafeError(emptyErrorMessage);
    }

    const anyEmptyLabels = field_options.some((option) => !option.label);
    if (anyEmptyLabels) {
      return new WebsafeError(invalidOptionErrorMessage);
    }
  };

  if (type === GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI) {
    const errors = checkOptionsForErrors(
      errorStrings.INVALID_MULTI_OPTIONS,
      errorStrings.INVALID_MULTI_OPTIONS_LABEL
    );
    if (errors) {
      return errors;
    }
  }

  if (type === GECKO_CONSTS.FIELD_TYPE_SELECT) {
    const errors = checkOptionsForErrors(
      errorStrings.INVALID_SELECT_OPTIONS,
      errorStrings.INVALID_SELECT_OPTIONS_LABEL
    );
    if (errors) {
      return errors;
    }
  }

  const defaultValueError = validateDefaultValue(type, default_value, field);
  if (defaultValueError) {
    return defaultValueError;
  }

  if (!validateKey(key)) {
    return new WebsafeError(errorStrings.INVALID_KEY);
  }

  return hasDuplicateField(field, fields);
};

export default {
  getValidationErrors,
};
